import * as constants from 'constants/intl-constants';
import * as Immutable from 'immutable';
import {createReducer} from 'redux-store';

import type {LocaleModule} from 'constants/intl-constants';
import type {Set} from 'immutable';
import type {ResolvedIntlConfig} from 'react-intl/src/types';
import type {ActionsMap} from 'redux-store';

export type IntlStore = {
    formats: Readonly<ResolvedIntlConfig['formats']>;
    messages: Readonly<ResolvedIntlConfig['messages']>;
    modules: Set<LocaleModule>;
};

export const formats: IntlStore['formats'] = {
    /**
     * Warning: do not specify any `timeZone` setting in these formats. Most
     * browsers do not support any value for `timeZone` other than 'UTC', so
     * we need to fall back to the local time to avoid throwing exceptions.
     */
    date: {
        longDateTime: {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        },
        longDateTimeWithTimezone: {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
            timeZoneName: 'short',
        },
        preciseDateTime: {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: true,
        },
        shortDateTime: {
            // Sep 20, 2019, 3:12 PM
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        },
        mediumDate: {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        },
        longDate: {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        },
        shortDate: {
            // en-US: 09/20/2019
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
        },
        date: {
            day: 'numeric',
        },
        month: {
            month: 'long',
        },
        monthAndDay: {
            month: 'short',
            day: 'numeric',
        },
        yearAndMonth: {
            year: 'numeric',
            month: 'short',
        },
        timeOfDay: {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        },
    },
};

const initialState = (): IntlStore => {
    return {
        formats,
        messages: {},
        modules: Immutable.Set(),
    };
};

const actionsMap: ActionsMap<IntlStore> = {
    [constants.SET_FORMATS]: (state, action) => {
        return {
            formats: {...state.formats, ...action.formats},
        };
    },
    [constants.SET_MESSAGES]: (state, action) => {
        return {
            messages: {...state.messages, ...action.messages},
            modules: state.modules.concat(action.moduleNames),
        };
    },
};

export default createReducer<IntlStore>(initialState(), actionsMap);
